import { DatePipe } from '@angular/common';
import { inject, Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BillingPeriodInterface, DurationPeriod, RevenuePeriod } from '@vendasta/sales-orders';
import dayjs from 'dayjs';

@Pipe({
  name: 'formatBillingPeriod',
  standalone: false,
})
export class FormatBillingPeriodPipe implements PipeTransform {
  private datePipe = inject(DatePipe);
  private translateService = inject(TranslateService);

  transform(billingPeriod?: BillingPeriodInterface, revenuePeriod?: RevenuePeriod, defaultStartDate?: Date): string {
    if (!billingPeriod) {
      return '';
    }

    const startDate =
      this.datePipe.transform(billingPeriod.startDate, 'MMM d, yyyy') ||
      this.datePipe.transform(defaultStartDate, 'MMM d, yyyy') ||
      '';
    if (billingPeriod.duration) {
      return this.formatDurationBillingPeriod(startDate, billingPeriod);
    }

    if (
      !revenuePeriod ||
      !billingPeriod.endDate ||
      dayjs(billingPeriod.endDate).isSame(0) ||
      dayjs(billingPeriod.endDate).isBefore(startDate)
    ) {
      return startDate;
    }

    if (billingPeriod.duration) {
      return this.formatDurationBillingPeriod(startDate, billingPeriod);
    }

    if (revenuePeriod === RevenuePeriod.YEARLY) {
      return this.formatYearlyBillingPeriod(startDate, billingPeriod.endDate);
    }

    return this.formatMonthlyBillingPeriod(startDate, billingPeriod.endDate);
  }

  private formatDurationBillingPeriod(startDate: string, billingPeriod: BillingPeriodInterface): string {
    const translationProps: BillingPeriodTranslationProps = {
      startDate,
      numPeriods: billingPeriod.duration?.value || 1,
    };

    if (!billingPeriod?.duration) {
      return startDate;
    }

    // Yearly duration periods
    if (billingPeriod.duration.duration === DurationPeriod.YEAR) {
      if (billingPeriod.duration.value === 1) {
        return this.translateService.instant('INVENTORY_UI.BILLING_PERIOD.START_DATE_ONE_YEAR', translationProps);
      }
      return this.translateService.instant('INVENTORY_UI.BILLING_PERIOD.START_DATE_MANY_YEARS', translationProps);
    }

    // Monthly duration periods
    if (billingPeriod.duration.value === 1) {
      return this.translateService.instant('INVENTORY_UI.BILLING_PERIOD.START_DATE_ONE_MONTH', translationProps);
    }

    return this.translateService.instant('INVENTORY_UI.BILLING_PERIOD.START_DATE_MANY_MONTHS', translationProps);
  }

  private formatMonthlyBillingPeriod(startDate: string, endDate: Date): string {
    const numMonths = dayjs(endDate).diff(startDate, 'month');
    const translationProps: BillingPeriodTranslationProps = { startDate, numPeriods: numMonths };
    if (numMonths <= 0) {
      return '';
    }
    if (numMonths === 1) {
      return this.translateService.instant('INVENTORY_UI.BILLING_PERIOD.START_DATE_ONE_MONTH', translationProps);
    }

    return this.translateService.instant('INVENTORY_UI.BILLING_PERIOD.START_DATE_MANY_MONTHS', translationProps);
  }

  private formatYearlyBillingPeriod(startDate: string, endDate: Date): string {
    const numYears = dayjs(endDate).diff(startDate, 'year');
    const translationProps: BillingPeriodTranslationProps = { startDate, numPeriods: numYears };
    if (numYears <= 0) {
      return '';
    }
    if (numYears === 1) {
      return this.translateService.instant('INVENTORY_UI.BILLING_PERIOD.START_DATE_ONE_YEAR', translationProps);
    }

    return this.translateService.instant('INVENTORY_UI.BILLING_PERIOD.START_DATE_MANY_YEARS', translationProps);
  }
}

export type BillingPeriodTranslationProps = {
  startDate: string;
  numPeriods: number;
};
